import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

// §7.1 共通ボタン。variant で押し込み物理・配色・厚みを切り替える。
// - primary: 画面の主要 CTA (1画面に原則1つ)
// - secondary: キャンセル・CSV出力等の二次操作
// - ghost: 表の行内操作
// - danger: 破壊的操作の確認ダイアログ内の実行ボタン
// - danger-outline: 使い勝手フィードバック⑤a (詳細設計 §6-7)。決裁モーダルの「却下する」用。
//   `danger` (塗り) と並べて表示すると「差し戻す (回復できる)」「却下 (終端)」の区別が付かない
//   3ボタン構成で使う。⚠️ 既存の `danger` の見た目は変えないこと (design-expert CRITICAL指摘)。
// - on-chrome: クローム (紺サイドバー・ヘッダー) 上に置くボタン
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline' | 'on-chrome';
export type ButtonSize = 'sm' | 'md';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** true の間、ラベルは維持し左端にスピナーを表示。幅は変えない (§5 レイアウトシフト禁止)。 */
  loading?: boolean;
  /** loading 中に表示するラベル (省略時は children をそのまま維持) */
  loadingLabel?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
};

// §7.1 サイズ: md=40px (padding 11px 20px), sm=34px (padding 8px 14px)。モバイルはタップ領域 44px 確保 (§7.1)
const sizeCls: Record<ButtonSize, string> = {
  md: 'h-10 px-5 text-[13px] min-h-[44px] sm:min-h-[40px]',
  sm: 'h-[34px] px-3.5 text-[12.5px] min-h-[44px] sm:min-h-[34px]',
};

const baseCls =
  'inline-flex items-center justify-center gap-2 font-extrabold rounded-md ' +
  'transition-colors focus-visible:outline-none focus:outline-none whitespace-nowrap';

// disabled は opacity .45 + cursor-not-allowed (§7.1)。厚みの影は残す
const disabledCls = 'disabled:opacity-45 disabled:cursor-not-allowed';

const variantCls: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-red text-white btn-press-primary hover:bg-brand-red-hover focus-ring',
  danger:
    'bg-brand-red text-white btn-press-danger hover:bg-brand-red-hover focus-ring',
  // ⚠️ `btn-press-secondary` は box-shadow の inset で中立色 (#E2DFDA) のリングを描くため、実際の
  // `border` (danger色) と重ねると二重の縁取りになる。この variant は 3D 押し込み演出を持たせず
  // border+背景色の変化のみで表現する (baseCls の transition-colors で hover は滑らかに変わる)。
  'danger-outline':
    'bg-white text-danger-text border border-danger-border hover:bg-danger-bg focus-ring',
  secondary:
    'bg-white text-text-body btn-press-secondary hover:bg-surface-sunken focus-ring',
  ghost:
    'bg-transparent text-text-body font-bold hover:bg-surface-sunken focus-ring',
  'on-chrome':
    'bg-chrome-raised text-white font-bold hover:bg-chrome-raised/80 focus-ring',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, loadingLabel, disabled, leftIcon, rightIcon, children, className = '', type = 'button', ...rest },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={`${baseCls} ${sizeCls[size]} ${variantCls[variant]} ${disabledCls} ${className}`}
      {...rest}
    >
      {loading ? (
        <Loader2 size={16} className="animate-spin shrink-0" aria-hidden />
      ) : leftIcon ? (
        <span className="shrink-0 inline-flex" aria-hidden>{leftIcon}</span>
      ) : null}
      {loadingLabel ? (
        // children と loadingLabel を CSS grid で同一セルに重ねて表示し、
        // 非表示側も visibility:hidden で幅計算に残すことでボタン自身が最大幅を保証する
        // (呼び出し側の w-full 指定に頼らない、§5 レイアウトシフト禁止)
        <span className="grid">
          <span className={`col-start-1 row-start-1 truncate ${loading ? 'invisible' : ''}`}>{children}</span>
          <span className={`col-start-1 row-start-1 truncate ${loading ? '' : 'invisible'}`}>{loadingLabel}</span>
        </span>
      ) : (
        <span className="truncate">{children}</span>
      )}
      {!loading && rightIcon && <span className="shrink-0 inline-flex" aria-hidden>{rightIcon}</span>}
    </button>
  );
});
